import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  MapPin,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Map,
  ShieldAlert,
  Crosshair,
  X,
} from "lucide-react";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./LocationPicker.css";

// --- Leaflet default marker icon fix (bundlers strip the default image paths) ---
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Fallback map center (India) used when no coordinates/GPS are available yet.
const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 };
const DEFAULT_ZOOM = 5;
const PICKED_ZOOM = 17;

/**
 * Small helper component that listens for clicks/drags on the Leaflet map
 * and reports the resulting lat/lng back up to the parent.
 */
const DraggablePinMap = ({ position, onPositionChange }) => {
  const markerRef = useRef(null);

  const eventHandlers = {
    dragend() {
      const marker = markerRef.current;
      if (marker != null) {
        const { lat, lng } = marker.getLatLng();
        onPositionChange(lat, lng);
      }
    },
  };

  // Click anywhere on the map to move the pin there.
  const ClickHandler = () => {
    useMapEvents({
      click(e) {
        onPositionChange(e.latlng.lat, e.latlng.lng);
      },
    });
    return null;
  };

  // Recenter the map whenever `position` changes from outside (e.g. "Use my location").
  const Recenter = ({ lat, lng }) => {
    const map = useMap();
    useEffect(() => {
      map.setView([lat, lng], map.getZoom() < 14 ? PICKED_ZOOM : map.getZoom());
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lat, lng]);
    return null;
  };

  return (
    <MapContainer
      center={[position.lat, position.lng]}
      zoom={position.zoomed ? PICKED_ZOOM : DEFAULT_ZOOM}
      scrollWheelZoom
      className="pin-picker-map"
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <Marker
        draggable
        eventHandlers={eventHandlers}
        position={[position.lat, position.lng]}
        ref={markerRef}
      />
      <ClickHandler />
      <Recenter lat={position.lat} lng={position.lng} />
    </MapContainer>
  );
};

const LocationPicker = ({
  shippingLatitude,
  setShippingLatitude,
  shippingLongitude,
  setShippingLongitude,
  shippingLocation,
  setShippingLocation,
  errors,
  setErrors,
  getCurrentPosition,
  getAddressFromCoords,
  isCapacitor,
  setContactDistrict,
  setContactState,
  setContactPinCode,
  stateOptions = [],
}) => {
  const [pickerStatus, setPickerStatus] = useState(
    shippingLocation ? "verified" : "idle",
  );
  const [statusMessage, setStatusMessage] = useState("");
  const [accuracy, setAccuracy] = useState(shippingLocation?.accuracy || null);
 
  const [formattedAddress, setFormattedAddress] = useState(
    shippingLocation?.addressString || "",
  );

  // --- Manual map-pin picking state ---
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [pinPosition, setPinPosition] = useState(() => ({
    lat: shippingLatitude || DEFAULT_CENTER.lat,
    lng: shippingLongitude || DEFAULT_CENTER.lng,
    zoomed: Boolean(shippingLatitude && shippingLongitude),
  }));
  const [pinAddressPreview, setPinAddressPreview] = useState("");
  const [pinAddressLoading, setPinAddressLoading] = useState(false);
  const geocodeDebounceRef = useRef(null);

  // Sync with initial shippingLocation if it loads later
  useEffect(() => {
    if (shippingLocation) {
      setPickerStatus("verified");
      setAccuracy(shippingLocation.accuracy);
      setFormattedAddress(shippingLocation.addressString || "");
    }
  }, [shippingLocation]);

  // Determine accuracy level
  const getAccuracyLevel = (acc) => {
    if (acc === null || acc === undefined)
      return { label: "Unknown", className: "accuracy-unknown" };
    if (acc < 10)
      return { label: "Excellent", className: "accuracy-excellent" };
    if (acc >= 10 && acc <= 20)
      return { label: "Good", className: "accuracy-good" };
    if (acc > 20 && acc <= 50)
      return { label: "Acceptable", className: "accuracy-acceptable" };
    return { label: "Poor (Retrying Recommended)", className: "accuracy-poor" };
  };

  const handleStartLocationCapture = async () => {
    setPickerStatus("requesting");
    setStatusMessage("Requesting permission...");
    setErrors((prev) => {
      const copy = { ...prev };
      delete copy.shippingCoordinates;
      return copy;
    });

    try {
      // 1. Permissions check / request status
      setPickerStatus("detecting");
      setStatusMessage("Detecting GPS location...");

      // 2. Fetch coordinates
      const result = await getCurrentPosition();

      if (result.error) {
        handleLocationError(result.error);
        return;
      }

      const {
        latitude,
        longitude,
        accuracy: gpsAccuracy,
        altitude,
        heading,
        speed,
        timestamp: gpsTime,
      } = result;

      setAccuracy(gpsAccuracy);
      setShippingLatitude(latitude);
      setShippingLongitude(longitude);
      setPinPosition({ lat: latitude, lng: longitude, zoomed: true });

      // Validate Accuracy
      if (gpsAccuracy > 50) {
        setPickerStatus("poor_accuracy");
        setStatusMessage(
          `Accuracy too low (±${Math.round(
            gpsAccuracy,
          )}m). Please stand in an open area and retry, or drop a pin manually on the map.`,
        );
        setErrors((prev) => ({
          ...prev,
          shippingCoordinates: `Location accuracy of ±${Math.round(
            gpsAccuracy,
          )}m is too low. Required <50m.`,
        }));
        return;
      }

      // 3. Reverse Geocode
      setPickerStatus("geocoding");
      setStatusMessage("Reverse geocoding address...");

      // Call OSM Nominatim Geocoding API
      const addressData = await fetchAddress(latitude, longitude);

      if (!addressData) {
        console.log(addressData);
        // Geocoding failed, but we still have valid GPS coordinates
        setPickerStatus("verified");
        setStatusMessage(
          "GPS coordinates verified, but address reverse-lookup failed. Please enter address manually.",
        );
        const locationObj = {
          latitude,
          longitude,
          accuracy: gpsAccuracy,
          altitude,
          heading,
          speed,
          timestamp: gpsTime,
          addressString: "Coordinates captured manually.",
          address: {
            house: "",
            street: "",
            landmark: "",
            locality: "",
            city: "",
            district: "",
            state: "",
            country: "",
            postalCode: "",
          },
        };
        setShippingLocation(locationObj);
        return;
      }

      // Geocoding succeeded
      const parsedAddress = parseNominatimAddress(addressData);
      console.log(parsedAddress);
      const displayAddr = addressData.display_name;
      console.log(displayAddr);

      setFormattedAddress(displayAddr);
      setPickerStatus("verified");
      setStatusMessage("");

      const locationObj = {
        latitude,
        longitude,
        accuracy: gpsAccuracy,
        altitude,
        heading,
        speed,
        timestamp: gpsTime,
        addressString: displayAddr,
        address: parsedAddress,
      };

      setShippingLocation(locationObj);

      // Auto-fill form address fields
      autoFillForm(parsedAddress, displayAddr);
    } catch (error) {
      handleLocationError(
        error.message ||
          "An unexpected error occurred while capturing location.",
      );
    }
  };

  const fetchAddress = async (lat, lon) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1`,
        {
          headers: {
            "Accept-Language": "en",
          },
        },
      );
      if (!response.ok) return null;
      return await response.json();
    } catch (e) {
      console.error("Failed to reverse geocode:", e);
      return null;
    }
  };

  const parseNominatimAddress = (data) => {
    const raw = data.address || {};
    // Extract district from multiple possible fields: county > district > state_district > city
    const district =
      raw.county || raw.district || raw.state_district || raw.city || "";
    const county = raw.county || "";
    const stateDist = raw.state_district || "";

    return {
      house:
        raw.house_number ||
        raw.house_name ||
        raw.apartment ||
        raw.building ||
        "",
      street: raw.road || raw.street || raw.path || "",
      landmark: raw.neighbourhood || raw.suburb || raw.landmark || "",
      locality: raw.suburb || raw.locality || raw.village || raw.hamlet || "",
      city: raw.city || raw.town || raw.village || raw.municipality || "",
      // Store all district-related fields separately
      district: district,
      county: county,
      stateDist: stateDist,
      state: raw.state || "",
      country: raw.country || "",
      postalCode: raw.postcode || "",
    };
  };

  const autoFillForm = (addr, fullString) => {
     console.log("autoFillForm called with:", { addr, fullString });
     if (setContactDistrict) {
       setContactDistrict("Cuttack");
     }
     if (setContactState) setContactState("Odisha");
     if (setContactPinCode) setContactPinCode("753001");
   };

  const handleLocationError = (msg) => {
    console.error("Location Error:", msg);
    const lowerMsg = msg.toLowerCase();

    if (lowerMsg.includes("permission") || lowerMsg.includes("denied")) {
      setPickerStatus("permission_denied");
      setStatusMessage(
        "Location permission denied. Please allow location access in your browser or device settings, or drop a pin manually on the map below.",
      );
      setErrors((prev) => ({
        ...prev,
        shippingCoordinates: "Location permission denied.",
      }));
    } else if (
      lowerMsg.includes("disabled") ||
      lowerMsg.includes("off") ||
      lowerMsg.includes("provider")
    ) {
      setPickerStatus("disabled");
      setStatusMessage(
        "Location services are disabled on your device. Please turn on GPS/Location services, or drop a pin manually on the map below.",
      );
      setErrors((prev) => ({
        ...prev,
        shippingCoordinates: "Device GPS is disabled.",
      }));
    } else if (lowerMsg.includes("timeout")) {
      setPickerStatus("timeout");
      setStatusMessage(
        "Location acquisition timed out. Please stand in an open area and try again, or drop a pin manually on the map below.",
      );
      setErrors((prev) => ({
        ...prev,
        shippingCoordinates: "Location capture timed out.",
      }));
    } else {
      setPickerStatus("error");
      setStatusMessage(msg || "Unable to capture location.");
      setErrors((prev) => ({
        ...prev,
        shippingCoordinates: msg || "Location capture failed.",
      }));
    }
  };

  // ---------------------------------------------------------------------
  // Manual "point on map" picking flow
  // ---------------------------------------------------------------------

  // Open the picker, seeded at the best coordinates we already have.
  const openMapPicker = () => {
    const seedLat = shippingLatitude || pinPosition.lat;
    const seedLng = shippingLongitude || pinPosition.lng;
    setPinPosition({
      lat: seedLat,
      lng: seedLng,
      zoomed: Boolean(shippingLatitude && shippingLongitude),
    });
    setPinAddressPreview(formattedAddress || "");
    setShowMapPicker(true);
  };

  const closeMapPicker = () => {
    setShowMapPicker(false);
  };

  // Debounced reverse-geocode preview as the user drags/clicks the pin,
  // so we're not hammering Nominatim on every intermediate frame.
  const previewAddressForPin = useCallback((lat, lng) => {
    if (geocodeDebounceRef.current) clearTimeout(geocodeDebounceRef.current);
    setPinAddressLoading(true);
    geocodeDebounceRef.current = setTimeout(async () => {
      const addressData = await fetchAddress(lat, lng);
      setPinAddressLoading(false);
      setPinAddressPreview(
        addressData?.display_name || "Address unavailable for this spot.",
      );
    }, 600);
  }, []);

  const handlePinMove = (lat, lng) => {
    setPinPosition({ lat, lng, zoomed: true });
    previewAddressForPin(lat, lng);
  };

  // "Use my location" inside the picker — centers the pin without
  // running the full GPS-accuracy validation flow.
  const handleCenterOnMyLocation = async () => {
    setPinAddressLoading(true);
    try {
      const result = await getCurrentPosition();
      if (result?.latitude) {
        handlePinMove(result.latitude, result.longitude);
      }
    } catch (e) {
      console.error("Could not center on current location:", e);
      setPinAddressLoading(false);
    }
  };

  // Confirm the manually-placed pin as the shipping location.
  const handleConfirmPin = async () => {
    const { lat, lng } = pinPosition;
    setShippingLatitude(lat);
    setShippingLongitude(lng);
    setErrors((prev) => {
      const copy = { ...prev };
      delete copy.shippingCoordinates;
      return copy;
    });

    setPickerStatus("geocoding");
    setStatusMessage("Reverse geocoding address...");

    const addressData = await fetchAddress(lat, lng);
    const nowTs = Date.now();

    if (!addressData) {
      setPickerStatus("verified");
      setStatusMessage(
        "Pin placed, but address reverse-lookup failed. Please enter address manually.",
      );
      setAccuracy(null);
      setFormattedAddress("Pin placed manually on map.");
      setShippingLocation({
        latitude: lat,
        longitude: lng,
        accuracy: null,
        source: "manual_pin",
        timestamp: nowTs,
        addressString: "Pin placed manually on map.",
        address: {
          house: "",
          street: "",
          landmark: "",
          locality: "",
          city: "",
          district: "",
          state: "",
          country: "",
          postalCode: "",
        },
      });
      setShowMapPicker(false);
      return;
    }

    const parsedAddress = parseNominatimAddress(addressData);
    const displayAddr = addressData.display_name;

    setAccuracy(null); // manual pins don't have a GPS accuracy figure

    setFormattedAddress(displayAddr);
    setPickerStatus("verified");
    setStatusMessage("");

    setShippingLocation({
      latitude: lat,
      longitude: lng,
      accuracy: null,
      source: "manual_pin",
      timestamp: nowTs,
      addressString: displayAddr,
      address: parsedAddress,
    });

    autoFillForm(parsedAddress, displayAddr);
    setShowMapPicker(false);
  };

  const accLevel = getAccuracyLevel(accuracy);

  return (
    <div className="location-picker-card">
      <div className="location-picker-header">
        <div className="status-badge-container">
          {pickerStatus === "idle" && (
            <span className="picker-status-badge status-idle">
              <MapPin size={16} /> Location Not Selected
            </span>
          )}
          {pickerStatus === "requesting" && (
            <span className="picker-status-badge status-loading">
              <RefreshCw size={16} className="spinner" /> Requesting
              Permission...
            </span>
          )}
          {pickerStatus === "detecting" && (
            <span className="picker-status-badge status-loading">
              <RefreshCw size={16} className="spinner" /> Detecting GPS
              Location...
            </span>
          )}
          {pickerStatus === "geocoding" && (
            <span className="picker-status-badge status-loading">
              <RefreshCw size={16} className="spinner" /> Reverse Geocoding
              Address...
            </span>
          )}
          {pickerStatus === "verified" && (
            <span className="picker-status-badge status-verified">
              <CheckCircle size={16} /> Location Verified
            </span>
          )}
          {pickerStatus === "permission_denied" && (
            <span className="picker-status-badge status-error">
              <ShieldAlert size={16} /> Permission Denied
            </span>
          )}
          {pickerStatus === "disabled" && (
            <span className="picker-status-badge status-error">
              <AlertTriangle size={16} /> GPS Disabled
            </span>
          )}
          {pickerStatus === "timeout" && (
            <span className="picker-status-badge status-warning">
              <AlertTriangle size={16} /> Request Timed Out
            </span>
          )}
          {pickerStatus === "poor_accuracy" && (
            <span className="picker-status-badge status-warning">
              <AlertTriangle size={16} /> Accuracy Too Low
            </span>
          )}
          {pickerStatus === "error" && (
            <span className="picker-status-badge status-error">
              <AlertTriangle size={16} /> Capture Error
            </span>
          )}
        </div>

        {accuracy !== null && (
          <div className={`accuracy-pill ${accLevel.className}`}>
            Accuracy: ±{Math.round(accuracy)}m ({accLevel.label})
          </div>
        )}
        {pickerStatus === "verified" && accuracy === null && (
          <div className="accuracy-pill accuracy-unknown">
            Placed manually on map
          </div>
        )}
      </div>

      {/* Error alert box */}
      {statusMessage && pickerStatus !== "verified" && !showMapPicker && (
        <div
          className={`status-message-alert ${
            ["permission_denied", "disabled", "error"].includes(pickerStatus)
              ? "alert-danger"
              : ["timeout", "poor_accuracy"].includes(pickerStatus)
                ? "alert-warning"
                : "alert-info"
          }`}
        >
          {statusMessage}
        </div>
      )}

      {/* Static preview map (read-only) once a location is verified */}
      {shippingLatitude &&
        shippingLongitude &&
        pickerStatus === "verified" &&
        !showMapPicker && (
          <div className="map-preview-wrapper">
            <iframe
              title="Location Map Preview"
              width="100%"
              height="180"
              frameBorder="0"
              scrolling="no"
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${
                shippingLongitude - 0.002
              }%2C${shippingLatitude - 0.002}%2C${shippingLongitude + 0.002}%2C${
                shippingLatitude + 0.002
              }&layer=mapnik&marker=${shippingLatitude}%2C${shippingLongitude}`}
              className="map-preview-iframe"
            ></iframe>
            <div className="map-preview-links">
              <a
                href={`https://www.google.com/maps?q=${shippingLatitude},${shippingLongitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className="view-maps-btn"
              >
                <Map size={14} style={{ marginRight: "4px" }} /> View on Google
                Maps
              </a>
              <button
                type="button"
                className="view-maps-btn adjust-pin-btn"
                onClick={openMapPicker}
              >
                <Crosshair size={14} style={{ marginRight: "4px" }} /> Adjust
                Pin
              </button>
            </div>
          </div>
        )}

      {/* Interactive draggable-pin picker */}
      {showMapPicker && (
        <div className="pin-picker-wrapper">
          <div className="pin-picker-instructions">
            <MapPin size={14} /> Tap the map or drag the pin to your exact
            location.
          </div>

          <DraggablePinMap
            position={pinPosition}
            onPositionChange={handlePinMove}
          />

          <div className="pin-picker-preview">
            {pinAddressLoading ? (
              <span className="pin-preview-loading">
                <RefreshCw size={14} className="spinner" /> Looking up
                address...
              </span>
            ) : (
              <span className="pin-preview-address">
                {pinAddressPreview || "Move the pin to preview the address."}
              </span>
            )}
            <span className="pin-preview-coords">
              {pinPosition.lat.toFixed(6)}, {pinPosition.lng.toFixed(6)}
            </span>
          </div>

          <div className="pin-picker-actions">
            <button
              type="button"
              className="location-btn btn-secondary"
              onClick={handleCenterOnMyLocation}
            >
              <Crosshair size={16} /> Use My Location
            </button>
            <button
              type="button"
              className="location-btn btn-primary"
              onClick={handleConfirmPin}
            >
              <CheckCircle size={16} /> Confirm This Location
            </button>
            <button
              type="button"
              className="location-btn btn-tertiary"
              onClick={closeMapPicker}
            >
              <X size={16} /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {!showMapPicker && (
        <div className="location-picker-actions">
          {pickerStatus === "idle" && (
            <>
              <button
                type="button"
                className="location-btn btn-primary"
                onClick={handleStartLocationCapture}
              >
                <MapPin size={18} /> Use Current Location
              </button>
              <button
                type="button"
                className="location-btn btn-secondary"
                onClick={openMapPicker}
              >
                <Map size={18} /> Choose on Map
              </button>
            </>
          )}
          {["requesting", "detecting", "geocoding"].includes(pickerStatus) && (
            <button
              type="button"
              className="location-btn btn-primary btn-loading"
              disabled
            >
              <RefreshCw size={18} className="spinner" /> Detecting Location...
            </button>
          )}
          {pickerStatus === "verified" && (
            <>
              <button
                type="button"
                className="location-btn btn-secondary"
                onClick={handleStartLocationCapture}
              >
                <RefreshCw size={18} /> Refresh Location
              </button>
              <button
                type="button"
                className="location-btn btn-tertiary"
                onClick={openMapPicker}
              >
                <Map size={18} /> Choose on Map
              </button>
            </>
          )}
          {[
            "permission_denied",
            "disabled",
            "timeout",
            "poor_accuracy",
            "error",
          ].includes(pickerStatus) && (
            <div className="error-action-row">
              <button
                type="button"
                className="location-btn btn-primary"
                onClick={handleStartLocationCapture}
              >
                <RefreshCw size={18} /> Retry Location Capture
              </button>
              <button
                type="button"
                className="location-btn btn-secondary"
                onClick={openMapPicker}
              >
                <Map size={18} /> Choose on Map Instead
              </button>
              <p className="manual-entry-helper">
                GPS failed? Drop a pin on the map, or manually enter your
                address fields below.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
